import React, { useState } from "react";
import { Box, Card, Typography, Tooltip, Chip, Skeleton } from "@mui/material";
import { formatAmount } from "../utils/format";

// Shiny Shimmer Skeleton Component for Map
const MapShinySkeleton = ({ width = "100%", height = 20, borderRadius = 6 }) => (
  <Box
    sx={{
      width,
      height,
      borderRadius: typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 35%, #ffffff 50%, #f8fafc 65%, #e2e8f0 100%)",
      backgroundSize: "400% 100%",
      animation: "mapShinyShimmer 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      "@keyframes mapShinyShimmer": {
        "0%": { backgroundPosition: "100% 0%" },
        "100%": { backgroundPosition: "0% 0%" },
      },
    }}
  />
);

/**
 * UAESalesMap component
 * Renders an interactive vector SVG map of the 7 UAE Emirates paired with
 * a detailed breakdown list matching the dashboard design.
 */
const UAESalesMap = ({ data = [], totalSales = 0, loading = false }) => {
  const [hoveredEmirate, setHoveredEmirate] = useState(null);
  // Clicked emirate stays highlighted (click again to clear); hover previews another.
  const [selectedEmirate, setSelectedEmirate] = useState(null);
  const activeEmirate = hoveredEmirate || selectedEmirate;
  const toggleSelected = (key) => setSelectedEmirate((cur) => (cur === key ? null : key));

  // Standard Emirate definitions matching official UAE reference map color palette
  const defaultEmirates = [
    { name: "Dubai", key: "dubai", color: "#8bce73" },      // Soft Leaf Green
    { name: "Abu Dhabi", key: "abudhabi", color: "#f7db69" }, // Golden Yellow
    { name: "Sharjah", key: "sharjah", color: "#f28588" },   // Coral Pink / Red
    { name: "Ajman", key: "ajman", color: "#bd93c4" },     // Lavender Purple
    { name: "Ras Al Khaimah", key: "rak", color: "#3bb396" },// Emerald Teal Green
    { name: "Others", key: "others", color: "#f59b4a" },     // Warm Orange (Fujairah / UQ)
  ];

  const hasRealData = Array.isArray(data) && data.length > 0;

  // Map incoming data or use structured breakdown
  const emirateList = defaultEmirates.map((item) => {
    const found = (data || []).find(
      (d) => d.emirate && d.emirate.toLowerCase().includes(item.name.toLowerCase())
    );
    const sales = found ? parseFloat(found.total_sales || 0) : 0;
    const calcPct = totalSales > 0 ? (sales / totalSales) * 100 : 0;

    if (hasRealData) {
      return {
        ...item,
        sales: sales,
        pct: Math.round(calcPct),
      };
    }

    return {
      ...item,
      sales: 0,
      pct: 0,
    };
  });

  // Geographically accurate SVG paths for the UAE Emirates
  const emiratePaths = {
    abudhabi: {
      d: "M 44.8,144.3 L 43.3,144.3 L 44.1,141.9 L 45.4,142.4 L 44.8,144.3 Z M 167.4,146.8 L 169.4,148.1 L 173.5,149.4 L 174.1,151.4 L 173.2,153.1 L 171.1,153.7 L 168.7,153.6 L 166.8,152.7 L 166.4,151.1 L 165.7,150.9 L 165.4,152.7 L 163.6,153.8 L 161.4,154.2 L 159.2,153.9 L 155.4,152.2 L 152.5,151.8 L 151.7,151.0 L 153.6,149.4 L 156.0,148.5 L 158.1,146.9 L 159.8,146.6 L 161.3,147.2 L 163.4,145.7 L 165.9,144.6 L 167.4,146.8 Z M 189.8,145.9 L 189.2,147.8 L 188.6,146.5 L 184.1,144.5 L 183.4,142.6 L 184.3,141.1 L 185.8,140.5 L 189.1,144.1 L 189.8,145.9 Z M 193.1,143.8 L 191.8,143.9 L 188.8,141.4 L 189.0,139.8 L 190.2,138.7 L 192.2,138.4 L 193.3,140.7 L 194.8,142.4 L 193.1,143.8 Z M 84.2,142.1 L 83.1,143.1 L 82.2,142.2 L 80.8,139.2 L 81.0,137.7 L 83.0,135.4 L 84.6,134.5 L 86.2,135.0 L 86.8,136.2 L 87.0,139.2 L 84.2,142.1 Z M 199.0,138.1 L 198.3,141.2 L 197.1,141.7 L 195.3,138.0 L 197.4,138.7 L 196.1,136.5 L 197.0,136.4 L 199.0,138.1 Z M 138.7,139.3 L 134.8,141.2 L 132.3,142.0 L 131.6,143.9 L 130.7,142.7 L 128.6,141.9 L 127.9,143.0 L 124.4,142.4 L 122.6,142.7 L 123.2,140.7 L 125.7,141.3 L 132.2,138.6 L 133.4,139.8 L 134.7,139.9 L 134.4,137.6 L 137.0,134.0 L 138.2,133.2 L 138.6,134.7 L 140.1,135.6 L 139.6,138.4 L 138.7,139.3 Z M 208.0,133.8 L 205.4,133.8 L 199.1,131.6 L 199.1,130.5 L 203.2,126.7 L 202.8,128.2 L 203.2,129.5 L 206.8,131.8 L 208.0,133.8 Z M 66.0,126.1 L 65.2,126.4 L 63.2,124.8 L 63.1,123.8 L 65.0,121.8 L 66.5,122.9 L 66.9,124.6 L 66.0,126.1 Z M 27.1,116.1 L 27.4,116.7 L 26.6,118.5 L 26.2,116.4 L 27.1,116.1 Z M 117.0,99.2 L 116.7,100.6 L 115.7,99.2 L 115.5,97.7 L 116.8,97.9 L 117.0,99.2 Z M 193.4,74.3 L 193.9,76.8 L 192.2,76.4 L 191.2,75.1 L 192.0,73.8 L 193.4,74.3 Z M 296.3,115.6 L 297.4,119.2 L 295.6,120.9 L 295.2,123.3 L 293.5,125.3 L 294.4,129.9 L 294.4,134.9 L 296.8,137.6 L 296.5,141.1 L 293.5,146.7 L 293.0,149.1 L 299.1,150.5 L 302.8,149.0 L 304.8,149.9 L 306.6,155.6 L 309.0,160.0 L 308.3,161.1 L 304.1,162.1 L 296.3,165.2 L 295.1,165.0 L 293.4,163.7 L 291.1,164.3 L 285.7,163.9 L 281.9,164.9 L 278.7,166.6 L 275.9,167.7 L 273.0,168.2 L 273.3,170.6 L 276.8,173.0 L 277.8,174.7 L 278.3,182.2 L 278.0,183.2 L 274.2,189.0 L 271.9,197.9 L 267.2,203.9 L 266.4,206.2 L 264.9,213.4 L 263.4,217.9 L 256.9,228.9 L 254.7,235.9 L 254.5,238.0 L 254.7,248.6 L 253.9,259.1 L 249.4,264.9 L 248.3,265.0 L 79.5,238.9 L 76.6,237.1 L 16.2,153.5 L 15.0,149.9 L 15.4,143.3 L 15.1,140.5 L 16.6,138.5 L 16.8,135.3 L 15.7,132.7 L 17.1,131.2 L 19.0,134.6 L 19.0,140.3 L 19.4,142.8 L 20.3,143.5 L 21.3,142.5 L 22.0,139.2 L 23.4,143.2 L 24.9,143.3 L 26.5,139.4 L 27.8,138.5 L 29.1,140.7 L 29.2,142.2 L 28.4,146.9 L 29.1,152.1 L 29.0,155.4 L 30.4,159.7 L 35.2,160.4 L 38.2,162.6 L 38.5,160.9 L 44.6,161.1 L 47.5,163.0 L 50.9,162.6 L 53.3,163.0 L 54.4,162.5 L 58.0,162.6 L 61.7,160.7 L 65.8,160.4 L 68.4,158.3 L 70.4,157.8 L 72.8,156.6 L 73.7,155.1 L 75.7,154.3 L 77.6,152.7 L 81.7,150.4 L 82.2,147.0 L 83.5,146.3 L 85.7,147.8 L 88.0,151.2 L 91.4,150.8 L 95.5,151.9 L 99.3,151.0 L 100.8,151.6 L 106.4,152.2 L 109.5,151.3 L 110.0,150.3 L 114.4,153.6 L 117.1,152.5 L 119.6,153.8 L 119.5,150.6 L 121.0,150.3 L 124.3,152.5 L 126.7,155.6 L 130.4,155.9 L 139.0,154.4 L 141.7,156.4 L 144.4,156.2 L 147.2,158.1 L 148.4,159.8 L 151.6,159.3 L 153.4,159.9 L 156.5,158.4 L 158.5,159.0 L 166.6,159.3 L 169.0,158.5 L 173.1,156.4 L 177.3,155.8 L 181.8,154.1 L 184.6,153.7 L 186.6,152.2 L 188.9,149.5 L 193.3,148.3 L 198.0,144.8 L 202.0,146.1 L 205.0,143.4 L 206.3,141.7 L 209.0,136.5 L 208.9,133.8 L 209.9,133.3 L 209.1,132.3 L 212.7,133.3 L 210.9,130.3 L 214.9,132.4 L 214.8,130.5 L 213.5,129.8 L 210.0,129.3 L 204.6,125.8 L 209.2,123.4 L 209.7,125.4 L 211.0,127.4 L 212.5,128.6 L 214.1,128.4 L 212.8,127.3 L 215.5,127.9 L 216.7,126.5 L 219.2,121.5 L 217.9,119.8 L 219.7,119.2 L 220.4,117.2 L 221.6,116.6 L 219.7,111.8 L 220.5,110.0 L 223.1,108.2 L 224.2,106.7 L 226.3,107.7 L 228.2,106.9 L 227.7,104.8 L 234.9,100.7 L 241.6,95.8 L 243.2,95.2 L 244.6,95.7 L 252.4,119.2 L 253.9,121.7 L 256.5,122.3 L 270.6,121.9 L 274.4,120.8 L 278.9,118.3 L 286.2,115.4 L 287.8,114.4 L 292.9,115.8 L 296.3,115.6 Z",
      label: "Abu Dhabi",
    },
    dubai: {
      d: "M 252.9,84.5 L 254.3,85.7 L 254.0,85.9 L 252.7,84.6 L 252.9,84.5 Z M 320.9,106.3 L 319.8,109.0 L 318.3,110.8 L 317.3,112.7 L 314.5,113.1 L 314.5,111.5 L 316.5,106.6 L 320.8,106.3 L 320.9,106.3 Z M 243.2,95.2 L 241.3,94.2 L 240.9,92.6 L 241.7,91.1 L 241.2,93.7 L 243.5,95.1 L 244.2,94.8 L 243.1,92.8 L 242.2,92.2 L 243.9,91.6 L 243.2,92.4 L 244.4,94.7 L 245.4,92.8 L 245.0,94.4 L 247.0,93.3 L 246.7,91.8 L 249.1,91.7 L 254.2,85.9 L 261.0,77.5 L 261.3,75.3 L 262.6,74.2 L 263.6,74.4 L 264.9,76.8 L 263.7,79.0 L 265.6,78.5 L 266.0,77.2 L 264.0,72.6 L 265.4,70.9 L 268.9,72.0 L 270.8,71.8 L 273.6,71.0 L 275.2,72.9 L 277.3,74.2 L 278.8,75.6 L 283.5,78.8 L 285.3,81.4 L 287.1,89.4 L 287.0,90.3 L 284.6,93.4 L 286.2,97.4 L 287.2,100.9 L 287.4,104.7 L 288.5,110.0 L 288.4,112.2 L 287.8,114.4 L 286.2,115.4 L 278.9,118.3 L 274.4,120.8 L 270.6,121.9 L 256.5,122.3 L 253.9,121.7 L 252.4,119.2 L 244.6,95.7 L 243.2,95.2 Z",
      label: "Dubai",
    },
    sharjah: {
      d: "M 328.4,75.2 L 327.1,75.6 L 326.9,73.7 L 327.8,73.3 L 328.7,74.0 L 328.4,75.2 Z M 327.8,99.9 L 326.7,99.8 L 326.3,101.3 L 323.1,100.8 L 321.6,99.2 L 322.1,96.6 L 321.0,93.1 L 319.7,91.0 L 320.5,89.8 L 321.7,90.3 L 326.4,95.3 L 327.4,98.0 L 327.5,98.7 L 327.8,99.9 Z M 328.2,97.2 L 328.8,95.1 L 329.5,94.3 L 329.8,92.2 L 329.4,91.5 L 328.5,88.0 L 329.7,87.8 L 333.6,88.7 L 333.5,89.5 L 334.9,95.2 L 331.3,95.6 L 330.7,96.4 L 330.5,98.7 L 329.2,98.3 L 328.2,97.2 Z M 333.8,63.1 L 334.1,65.0 L 333.5,67.1 L 335.0,70.4 L 333.2,71.8 L 332.8,72.4 L 330.2,71.8 L 328.9,70.0 L 326.9,69.7 L 327.7,67.7 L 330.9,65.6 L 333.8,63.1 Z M 305.1,68.3 L 306.2,69.9 L 307.1,68.0 L 307.6,65.7 L 308.5,65.3 L 309.9,66.0 L 308.7,69.5 L 307.6,70.4 L 307.5,71.3 L 308.3,73.1 L 309.0,75.3 L 308.9,77.2 L 307.2,79.0 L 306.7,80.7 L 308.3,83.0 L 306.5,87.4 L 306.4,90.3 L 307.7,93.3 L 308.0,95.5 L 305.4,95.1 L 302.8,95.7 L 300.5,97.2 L 298.3,99.1 L 296.3,101.7 L 295.6,103.9 L 296.1,109.5 L 295.8,113.5 L 296.3,115.6 L 292.9,115.8 L 287.8,114.4 L 288.4,112.2 L 288.5,110.0 L 287.4,104.7 L 287.2,100.9 L 286.2,97.4 L 284.6,93.4 L 287.0,90.3 L 287.1,89.4 L 285.3,81.4 L 283.5,78.8 L 278.8,75.6 L 277.3,74.2 L 275.2,72.9 L 273.6,71.0 L 270.8,71.8 L 268.9,72.0 L 265.4,70.9 L 266.4,69.8 L 268.0,69.7 L 267.7,67.9 L 268.9,66.7 L 269.6,67.3 L 272.0,64.6 L 273.6,65.7 L 277.8,66.9 L 283.4,67.3 L 284.2,66.9 L 285.7,63.4 L 284.7,62.8 L 280.8,62.0 L 276.5,59.9 L 277.5,58.9 L 276.7,58.4 L 278.1,56.1 L 282.6,58.9 L 285.3,61.1 L 289.9,63.1 L 294.2,66.7 L 295.5,68.5 L 295.9,70.4 L 297.1,72.7 L 298.1,73.3 L 300.8,73.2 L 303.5,72.1 L 304.2,70.9 L 303.6,68.7 L 305.1,68.3 Z",
      label: "Sharjah",
    },
    ajman: {
      d: "M 272.0,64.6 L 273.0,63.3 L 274.1,64.3 L 274.8,62.8 L 275.2,63.3 L 276.5,59.9 L 280.8,62.0 L 284.7,62.8 L 285.7,63.4 L 284.2,66.9 L 283.4,67.3 L 277.8,66.9 L 273.6,65.7 L 272.0,64.6 Z M 308.7,69.5 L 310.3,71.0 L 310.0,71.7 L 308.3,73.1 L 307.5,71.3 L 307.6,70.4 L 308.7,69.5 Z",
      label: "Ajman",
    },
    rak: {
      d: "M 293.4,45.5 L 295.0,43.7 L 296.4,43.4 L 297.8,42.3 L 299.7,41.8 L 302.1,39.9 L 305.7,35.4 L 307.5,33.9 L 305.9,37.3 L 307.0,37.6 L 308.3,32.5 L 311.1,30.0 L 312.1,28.4 L 312.9,26.0 L 310.6,27.9 L 312.4,25.1 L 313.8,22.0 L 315.3,16.8 L 316.8,16.7 L 320.1,15.8 L 320.9,15.9 L 322.2,20.2 L 321.6,23.7 L 321.3,30.2 L 319.6,33.0 L 319.5,35.3 L 321.0,37.3 L 320.8,39.2 L 319.3,40.6 L 319.5,45.1 L 320.0,46.0 L 319.1,46.6 L 316.4,47.3 L 314.5,47.1 L 313.0,48.1 L 311.4,49.8 L 310.1,50.3 L 308.5,50.3 L 307.7,51.8 L 308.8,57.7 L 310.7,58.4 L 314.1,58.3 L 315.7,57.4 L 316.8,57.4 L 318.3,59.9 L 317.7,61.2 L 315.9,63.4 L 316.4,64.4 L 318.0,64.5 L 318.7,65.2 L 318.6,67.2 L 316.7,68.5 L 314.9,68.8 L 311.7,67.8 L 309.9,66.0 L 308.5,65.3 L 307.6,65.7 L 307.1,68.0 L 306.2,69.9 L 305.1,68.3 L 302.0,65.0 L 302.5,63.3 L 302.1,61.9 L 299.7,59.2 L 299.0,57.3 L 298.7,54.0 L 297.0,48.1 L 295.0,47.0 L 293.4,45.5 Z M 327.7,102.9 L 325.5,105.0 L 324.3,105.6 L 320.9,106.3 L 320.8,106.3 L 316.5,106.6 L 312.9,103.2 L 311.3,102.0 L 310.7,98.8 L 310.0,96.7 L 308.0,95.5 L 307.7,93.3 L 306.4,90.3 L 306.5,87.4 L 308.3,83.0 L 306.7,80.7 L 307.2,79.0 L 308.9,77.2 L 313.1,77.7 L 314.3,77.4 L 316.9,77.8 L 319.0,76.5 L 319.4,74.4 L 321.3,72.9 L 321.5,72.1 L 319.9,72.0 L 318.3,70.8 L 320.5,69.4 L 322.6,70.0 L 323.3,71.3 L 325.2,71.0 L 325.9,73.5 L 323.5,75.2 L 323.4,77.2 L 320.7,77.7 L 320.3,78.5 L 320.6,81.6 L 319.8,86.2 L 319.8,87.7 L 320.5,89.8 L 319.7,91.0 L 321.0,93.1 L 322.1,96.6 L 321.6,99.2 L 323.1,100.8 L 326.3,101.3 L 327.4,102.1 L 327.7,102.9 Z",
      label: "Ras Al Khaimah",
    },
    others: {
      d: "M 278.1,56.1 L 278.7,54.0 L 280.5,51.4 L 281.6,51.0 L 279.9,54.0 L 281.3,55.4 L 285.4,54.5 L 287.5,50.1 L 290.3,47.4 L 292.6,46.6 L 293.4,45.5 L 295.0,47.0 L 297.0,48.1 L 298.7,54.0 L 299.0,57.3 L 299.7,59.2 L 302.1,61.9 L 302.5,63.3 L 302.0,65.0 L 305.1,68.3 L 303.6,68.7 L 304.2,70.9 L 303.5,72.1 L 300.8,73.2 L 298.1,73.3 L 297.1,72.7 L 295.9,70.4 L 295.5,68.5 L 294.2,66.7 L 289.9,63.1 L 285.3,61.1 L 282.6,58.9 L 278.1,56.1 Z M 330.3,100.0 L 329.9,101.2 L 327.7,102.9 L 327.4,102.1 L 326.3,101.3 L 326.7,99.8 L 327.8,99.9 L 329.8,100.3 L 330.3,100.0 Z M 320.5,89.8 L 319.8,87.7 L 319.8,86.2 L 320.6,81.6 L 320.3,78.5 L 320.7,77.7 L 323.4,77.2 L 325.3,78.3 L 326.4,77.9 L 329.1,75.9 L 332.4,74.4 L 332.8,72.4 L 333.2,71.8 L 335.0,70.4 L 334.5,75.4 L 333.8,78.5 L 333.6,88.7 L 329.7,87.8 L 328.5,88.0 L 329.4,91.5 L 329.8,92.2 L 329.5,94.3 L 328.8,95.1 L 328.2,97.2 L 327.4,98.0 L 326.4,95.3 L 321.7,90.3 L 320.5,89.8 Z M 326.9,69.7 L 325.2,71.0 L 323.3,71.3 L 322.6,70.0 L 320.5,69.4 L 318.3,70.8 L 319.9,72.0 L 321.5,72.1 L 321.3,72.9 L 319.4,74.4 L 319.0,76.5 L 316.9,77.8 L 314.3,77.4 L 313.1,77.7 L 308.9,77.2 L 309.0,75.3 L 308.3,73.1 L 310.0,71.7 L 310.3,71.0 L 308.7,69.5 L 309.9,66.0 L 311.7,67.8 L 314.9,68.8 L 316.7,68.5 L 318.6,67.2 L 318.7,65.2 L 318.0,64.5 L 316.4,64.4 L 315.9,63.4 L 317.7,61.2 L 318.3,59.9 L 316.8,57.4 L 315.7,57.4 L 314.1,58.3 L 310.7,58.4 L 308.8,57.7 L 307.7,51.8 L 308.5,50.3 L 310.1,50.3 L 311.4,49.8 L 313.0,48.1 L 314.5,47.1 L 316.4,47.3 L 319.1,46.6 L 320.0,46.0 L 322.4,46.9 L 323.5,48.9 L 324.5,49.2 L 328.3,48.3 L 329.7,49.1 L 332.3,49.9 L 332.8,50.8 L 334.2,55.7 L 333.8,63.1 L 330.9,65.6 L 327.7,67.7 L 326.9,69.7 Z M 327.4,98.0 L 328.2,97.2 L 329.2,98.3 L 330.5,98.7 L 330.3,100.0 L 329.8,100.3 L 327.8,99.9 L 327.5,98.7 L 327.4,98.0 Z M 316.5,106.6 L 314.5,111.5 L 314.5,113.1 L 313.6,113.0 L 311.6,111.3 L 309.7,106.3 L 307.2,104.1 L 307.0,102.1 L 308.2,101.8 L 309.9,102.4 L 311.5,103.6 L 311.3,102.0 L 312.9,103.2 L 316.5,106.6 Z",
      label: "Fujairah & Others",
    },
  };

  const formatCurrency = (val) =>
    `AED ${formatAmount(val)}`;

  return (
    <Card
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2.5,
        border: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
          Sales Distribution (UAE)
        </Typography>
        <Chip
          label="Emirates Region"
          size="small"
          variant="outlined"
          color="primary"
          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
        />
      </Box>

      {loading ? (
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems="center" gap={2} flex={1} py={1}>
          <Box width={{ xs: "100%", sm: "48%" }} display="flex" justifyContent="center">
            <MapShinySkeleton width="90%" height={180} borderRadius={12} />
          </Box>
          <Box width={{ xs: "100%", sm: "48%" }} display="flex" flexDirection="column" gap={1}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <MapShinySkeleton key={i} height={22} borderRadius={6} />
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          flex={1}
          sx={{ minHeight: 0 }}
        >
        {/* Interactive Vector SVG Map */}
        <Box
          sx={{
            width: { xs: "100%", sm: "48%" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            p: 0.5,
          }}
        >
          <svg
            viewBox="0 0 350 280"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: 200,
              filter: "drop-shadow(0px 4px 12px rgba(15, 23, 42, 0.08))",
            }}
          >
            {Object.entries(emiratePaths)
              .sort(([keyA], [keyB]) => (keyA === activeEmirate ? 1 : keyB === activeEmirate ? -1 : 0))
              .map(([key, path]) => {
                const matched = emirateList.find((e) => e.key === key);
                const isHovered = activeEmirate === key;
                const hasSales = matched && matched.sales > 0;
                const fillColor = matched ? matched.color : "#64748b";

                return (
                  <g
                    key={key}
                    onMouseEnter={() => setHoveredEmirate(key)}
                    onMouseLeave={() => setHoveredEmirate(null)}
                    onClick={() => toggleSelected(key)}
                    style={{ cursor: "pointer" }}
                  >
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" fontWeight="700" display="block">
                            {path.label}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {formatCurrency(matched?.sales)} ({matched?.pct}%)
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <path
                        d={path.d}
                        fill={fillColor}
                        fillOpacity={isHovered ? 1.0 : (hasSales ? 0.92 : 0.65)}
                        stroke="#ffffff"
                        strokeWidth={isHovered ? 2 : 1.5}
                        strokeLinejoin="round"
                        style={{
                          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                          transformOrigin: "center",
                          transform: isHovered ? "translateY(-3px) scale(1.04)" : "scale(1)",
                          filter: isHovered ? "drop-shadow(0px 8px 16px rgba(15, 23, 42, 0.22))" : "none",
                        }}
                      />
                    </Tooltip>
                  </g>
                );
              })}
          </svg>
        </Box>

        {/* Emirate Breakdown List */}
        <Box
          sx={{
            width: { xs: "100%", sm: "52%" },
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {/* Hide emirates with no sales once there is real data */}
          {emirateList.filter((item) => !hasRealData || totalSales <= 0 || item.sales > 0 || item.key === activeEmirate).map((item) => {
            const isHovered = activeEmirate === item.key;
            return (
              <Box
                key={item.key}
                onMouseEnter={() => setHoveredEmirate(item.key)}
                onMouseLeave={() => setHoveredEmirate(null)}
                onClick={() => toggleSelected(item.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 0.5,
                  px: 1,
                  borderRadius: 1.5,
                  backgroundColor: isHovered ? "#f1f5f9" : "transparent",
                  transition: "background-color 0.2s ease",
                  cursor: "pointer",
                }}
              >
                {/* Color Dot & Name */}
                <Box display="flex" alignItems="center" gap={1.25}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      boxShadow: isHovered ? `0 0 8px ${item.color}` : "none",
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={isHovered ? "700" : "600"}
                    color="#1e293b"
                    sx={{ fontSize: "0.82rem" }}
                  >
                    {item.name}
                  </Typography>
                </Box>

                {/* Amount & Percentage */}
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography
                    variant="body2"
                    fontWeight="700"
                    color="#0f172a"
                    sx={{ fontSize: "0.82rem" }}
                  >
                    {formatCurrency(item.sales)}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight="700"
                    sx={{
                      color: "#2563eb",
                      minWidth: 32,
                      textAlign: "right",
                      fontSize: "0.78rem",
                    }}
                  >
                    {item.pct}%
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      )}
    </Card>
  );
};

export default UAESalesMap;
